import { find, includes, isEmpty, isNull } from 'es-toolkit/compat';

import { Coordinates, Position, core } from '@tock/coretypes';
import { Lazy, lazy } from '@tock/shared/utils/lazy';
import { PlacesAutocompleteType } from 'components/inputs/LocationSuggestField';
import { TockCookieName } from '@tock/shared/constants/browser-storage/TockCookies';
import { getCookie, setCookie } from '@tock/shared/utils/cookieUtils';

export const AUTOSUGGEST_TYPE_ADDRESS: PlacesAutocompleteType[] = ['address'];

const geocoder: Lazy<google.maps.Geocoder> = lazy(() => new google.maps.Geocoder());
const autocompleter: Lazy<google.maps.places.AutocompleteService> = lazy(
  () => new google.maps.places.AutocompleteService()
);

// ---------------------------------------------------------------------------
// Modern Places API (flag-ON) data source
//
// The flag-ON path replaces the deprecated `AutocompleteService` /
// `Geocoder.geocode({placeId})` calls with `AutocompleteSuggestion` +
// `Place.fetchFields`, adapting the results back to the legacy
// `AutocompletePrediction` / `GeocoderResult` shapes the callers already consume
// so no UI/consumer changes are needed. These functions are only invoked when the
// caller passes `useNewApi = true` (threaded from `useIsVisglMapsEnabled()`); the
// flag-OFF path keeps the exact legacy behavior.
// ---------------------------------------------------------------------------

/**
 * Maps the legacy autocomplete `types` collections to modern
 * `includedPrimaryTypes` tokens (Table A/B, max 5). The legacy `'address'`
 * collection maps to street-level primary types; `'(cities)'`/`'(regions)'` map
 * to their closest primary-type equivalents. Returns `undefined` when nothing
 * maps, letting the request run unfiltered (matching the broad legacy behavior).
 */
function toIncludedPrimaryTypes(types?: string[]): string[] | undefined {
  if (!types || types.length === 0) {
    return undefined;
  }
  const mapped = new Set<string>();
  types.forEach((type) => {
    switch (type) {
      case 'address':
        // Legacy `types: ['address']` was Google's "precise address" collection,
        // which has no direct equivalent in the new API. Google's recommended
        // replacement is this broader set of precise-address primary types; the
        // previous `['street_address','premise']` was too narrow (it dropped
        // `subpremise` unit/apartment addresses and `route`), which returned a
        // different/worse result set than the legacy path.
        mapped.add('street_address');
        mapped.add('premise');
        mapped.add('subpremise');
        mapped.add('route');
        break;
      case '(cities)':
        mapped.add('locality');
        mapped.add('administrative_area_level_3');
        break;
      case '(regions)':
        mapped.add('postal_code');
        mapped.add('locality');
        break;
      default:
        // 'geocode', 'establishment', 'street_address', etc. pass through.
        mapped.add(type);
    }
  });
  const primaryTypes = Array.from(mapped).slice(0, 5);
  return primaryTypes.length > 0 ? primaryTypes : undefined;
}

/**
 * Adapts a modern `PlacePrediction` back to the legacy `AutocompletePrediction`
 * shape. Only the fields the callers read (`place_id`, `description`,
 * `structured_formatting.{main_text,secondary_text}`) are populated with real
 * data; the remaining required fields are filled with empty defaults.
 */
function placePredictionToLegacy(
  prediction: google.maps.places.PlacePrediction
): google.maps.places.AutocompletePrediction {
  return {
    place_id: prediction.placeId,
    description: prediction.text?.text ?? '',
    structured_formatting: {
      main_text: prediction.mainText?.text ?? prediction.text?.text ?? '',
      main_text_matched_substrings: [],
      secondary_text: prediction.secondaryText?.text ?? '',
    },
    matched_substrings: [],
    terms: [],
    types: prediction.types ?? [],
  } as unknown as google.maps.places.AutocompletePrediction;
}

async function fetchModernAutosuggestions(
  request: google.maps.places.AutocompletionRequest
): Promise<google.maps.places.AutocompletePrediction[]> {
  const country = request.componentRestrictions?.country;
  const includedRegionCodes = country ? (Array.isArray(country) ? country : [country]) : undefined;

  const suggestionRequest: google.maps.places.AutocompleteRequest = {
    input: request.input,
    includedPrimaryTypes: toIncludedPrimaryTypes(request.types),
    includedRegionCodes,
    // Forward the caller-provided bias unchanged. Both the legacy
    // `AutocompletionRequest` and the modern `AutocompleteRequest` use the
    // non-deprecated `locationBias`, so no `location`/`radius` translation is
    // needed (those were deprecated May 2023).
    locationBias: request.locationBias ?? undefined,
    sessionToken: request.sessionToken,
  };

  const { suggestions } =
    await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(suggestionRequest);

  return suggestions
    .map((suggestion) => suggestion.placePrediction)
    .filter((prediction): prediction is google.maps.places.PlacePrediction => prediction != null)
    .map(placePredictionToLegacy);
}

/**
 * Adapts a modern `Place` (from `fetchFields`) back to the legacy
 * `GeocoderResult` shape callers of {@link getAddressFromPlaceId} consume
 * (`address_components`, `place_id`, `formatted_address`, `geometry.location`,
 * `types`).
 */
function placeToGeocoderResult(place: google.maps.places.Place): google.maps.GeocoderResult {
  const addressComponents = (place.addressComponents ?? []).map((component) => ({
    long_name: component.longText ?? '',
    short_name: component.shortText ?? '',
    types: component.types,
  }));
  const location = place.location;
  if (!location) {
    // Callers (e.g. `geocodeResultToPatronLocation`) dereference
    // `result.geometry.location` unconditionally, so a missing location must
    // reject with a clear message rather than emitting a half-valid result that
    // throws an opaque `TypeError` downstream.
    throw new Error(`Place ${place.id} is missing a geometry location`);
  }
  return {
    address_components: addressComponents,
    place_id: place.id,
    formatted_address: place.formattedAddress ?? '',
    types: place.types ?? [],
    geometry: {
      location,
    },
  } as unknown as google.maps.GeocoderResult;
}

/**
 * `Place.fetchFields` field list requested when adapting a modern `Place` back
 * to the legacy `GeocoderResult`. `types` is required: callers such as
 * {@link geocodeResultToPatronLocation} branch on `GeocoderResult.types` to
 * classify a result as city/neighborhood/address, so omitting it silently
 * breaks that classification (this was the shipped bug). `fetchFields` is billed
 * per field, so this stays scoped to exactly what `placeToGeocoderResult` reads.
 */
export const MODERN_PLACE_ADDRESS_FIELDS: string[] = [
  'addressComponents',
  'location',
  'formattedAddress',
  'types',
];

async function fetchModernAddressFromPlaceId(placeId: string): Promise<google.maps.GeocoderResult> {
  const place = new google.maps.places.Place({ id: placeId });
  await place.fetchFields({ fields: MODERN_PLACE_ADDRESS_FIELDS });
  return placeToGeocoderResult(place);
}

export function isLocationDisplayable(location: core.ILocation | null) {
  if (isNull(location)) {
    return false;
  }
  return !(
    isEmpty(location.address) &&
    isEmpty(location.address2) &&
    isEmpty(location.city) &&
    isEmpty(location.country) &&
    isEmpty(location.googlePlacesId) &&
    isEmpty(location.state) &&
    isEmpty(location.name) &&
    isEmpty(location.zipCode)
  );
}

export function getLatLong(address: string): Promise<google.maps.GeocoderResult> {
  return getGeocodedAddress(address).then((results) => {
    return results[0];
  });
}

export function getCityLatLong(
  cityName: string,
  country: string
): Promise<google.maps.GeocoderResult> {
  return getGeocodedCity(cityName, country).then((results) => {
    return results[0];
  });
}

export function getGeocodedCity(
  cityName: string,
  country: string
): Promise<google.maps.GeocoderResult[]> {
  return getGeocodedResult({
    componentRestrictions: {
      administrativeArea: cityName,
      country: country,
    },
  });
}

export function getGeocodedAddress(address: string): Promise<google.maps.GeocoderResult[]> {
  return getGeocodedResult({
    address,
  });
}

export function getGeocodedLocation(
  lat: number,
  lng: number
): Promise<google.maps.GeocoderResult[]> {
  return getGeocodedResult({ location: { lat, lng } });
}

function getGeocodedResult(
  request: google.maps.GeocoderRequest
): Promise<google.maps.GeocoderResult[]> {
  return new Promise((resolve, reject) => {
    geocoder.get().geocode(request, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results) {
        resolve(results);
      } else {
        reject(status);
      }
    });
  });
}

/**
 * Performs a Google Maps API Places geocoding request to find the location
 * object that corresponds to the given place ID.
 */
export function getAddressFromPlaceId(
  placeId: string,
  useNewApi = false
): Promise<google.maps.GeocoderResult> {
  // TODO(visgl-burn-in): remove the `useNewApi` param and the legacy
  // `Geocoder.geocode` branch below (keep `fetchModernAddressFromPlaceId`) once
  // CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is fully rolled out.
  if (useNewApi) {
    return fetchModernAddressFromPlaceId(placeId);
  }
  return new Promise((resolve, reject) => {
    geocoder.get().geocode({ placeId: placeId }, function (results, status) {
      if (status === 'OK' && results && results[0]) {
        resolve(results[0]);
      } else {
        reject(status);
      }
    });
  });
}

export function getPlacesAutosuggestionsViaRequest(
  autoCompleteRequest: google.maps.places.AutocompletionRequest,
  useNewApi = false
): Promise<google.maps.places.AutocompletePrediction[]> {
  // TODO(visgl-burn-in): remove the `useNewApi` param and the legacy
  // `AutocompleteService` branch below (keep `fetchModernAutosuggestions`) once
  // CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is fully rolled out.
  if (useNewApi) {
    return fetchModernAutosuggestions(autoCompleteRequest);
  }
  const service = new google.maps.places.AutocompleteService();
  return new Promise((resolve, reject) => {
    service.getPlacePredictions(autoCompleteRequest, (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && results) {
        resolve(results);
      } else {
        reject(status);
      }
    });
  });
}

export function getPlacesAutosuggestions(
  address: string,
  types: string[],
  componentRestrictions?: google.maps.places.ComponentRestrictions,
  sessionToken?: google.maps.places.AutocompleteSessionToken,
  useNewApi = false
): Promise<google.maps.places.AutocompletePrediction[]> {
  const autoCompleteRequest: google.maps.places.AutocompletionRequest = {
    input: address,
    types,
    componentRestrictions,
    sessionToken,
  };

  // TODO(visgl-burn-in): remove the `useNewApi` param and the legacy
  // `autocompleter` branch below (keep `fetchModernAutosuggestions`) once
  // CONSUMER_ENABLE_GOOGLE_MAPS_API_UPDATE is fully rolled out.
  if (useNewApi) {
    return fetchModernAutosuggestions(autoCompleteRequest);
  }

  return new Promise((resolve, reject) => {
    autocompleter.get().getPlacePredictions(autoCompleteRequest, (results, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && results) {
        resolve(results);
      } else {
        reject(status);
      }
    });
  });
}

/**
 * @deprecated use /delivery/serviceability endpoint instead
 * Retrieves directions from the origin to destination. Multiple routes are returned, and each will have 1 leg.
 * We use this instead of the Distance Matrix API in order to get the shortest distance route.
 */
export function getDirections(
  origin: google.maps.Place | string,
  destination: google.maps.Place | string
): Promise<google.maps.DirectionsResult> {
  const service = new google.maps.DirectionsService();
  const request: google.maps.DirectionsRequest = {
    origin,
    destination: destination,
    travelMode: google.maps.TravelMode.DRIVING,
    provideRouteAlternatives: true,
  };

  return new Promise((resolve, reject) => {
    service.route(request, (results, status) => {
      if (
        status === google.maps.DirectionsStatus.OK ||
        status === google.maps.DirectionsStatus.NOT_FOUND ||
        status === google.maps.DirectionsStatus.ZERO_RESULTS
      ) {
        resolve(results ?? ({ routes: [] } as unknown as google.maps.DirectionsResult));
      } else {
        reject(status);
      }
    });
  });
}

export function placeResultToLocation(
  addressComponents: google.maps.GeocoderAddressComponent[],
  googlePlacesId: string
): core.ILocation {
  return getLocationComponentsFromGeocoderAddressComponent(addressComponents, googlePlacesId);
}

export function getLocationComponentsFromGeocoderAddressComponent(
  addressComponents: google.maps.GeocoderAddressComponent[],
  googlePlacesId: string
) {
  function addressComponent(field: string) {
    return find(addressComponents, (comp) => includes(comp.types, field))?.short_name ?? '';
  }

  function longAddressComponent(field: string) {
    return find(addressComponents, (comp) => includes(comp.types, field))?.long_name ?? '';
  }

  const country = addressComponent('country');
  const streetNumber = addressComponent('street_number');
  const streetName = addressComponent('route');
  const city =
    longAddressComponent('locality') ||
    longAddressComponent('postal_town') ||
    longAddressComponent('sublocality') ||
    longAddressComponent('administrative_area_level_3') ||
    longAddressComponent('neighborhood');
  const state =
    addressComponent('administrative_area_level_1') ||
    addressComponent('administrative_area_level_2');
  const zipCode = addressComponent('postal_code');

  return {
    address: `${streetNumber} ${streetName}`,
    city,
    state,
    country,
    zipCode,
    googlePlacesId,
  };
}

export function responsiblyGetCurrentPosition(
  resultOfUserAction = false
): Promise<Coordinates | undefined> {
  return new Promise((resolve) => {
    const hasAcceptedCookieName = TockCookieName.CURRENT_LOCATION_ACCEPTANCE;
    const shouldGetLocation = getCookie(hasAcceptedCookieName) === true || resultOfUserAction;
    const handleAcceptance = ({ coords }: Position) => {
      setCookie(hasAcceptedCookieName, true);
      resolve(coords);
    };
    const handleRejection = () => {
      setCookie(hasAcceptedCookieName, false);
      resolve(undefined);
    };
    if (!navigator.geolocation) {
      resolve(undefined);
    } else if (shouldGetLocation) {
      navigator.geolocation.getCurrentPosition(handleAcceptance, handleRejection);
    }
  });
}

export function getFormattedAddressFromStructuredAddress(
  address: string,
  city: string,
  state: string,
  country: string,
  zipCode: string
): string {
  return [address, city, `${state} ${zipCode}`, country].join(', ');
}
